'use client';

import { useMemo } from 'react';

import { FlatQuotationLine } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { FlyoutActions } from '@ravago/shared/radiance/components/flyout/components/Flyout/FlyoutActions';
import { FlyoutContent } from '@ravago/shared/radiance/components/flyout/components/Flyout/FlyoutContent';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { useQuotePricingState } from '../../../../hooks/use-quote-pricing-state';
import { getQuoteLineMtsLocalMap } from '../../../../utils/lead-time';
import { CombinedSuggestion, getDistinctQuoteQuantities } from '../../../../utils/quote-pricing/combined-pricing.utils';
import {
   getQuantityUnitOfMeasure,
   getQuoteSelectionMeta,
   getSuggestionRows,
   hasBracketRanges
} from '../../../../utils/quote-pricing/quote-selection.utils';
import { QuantitySelector } from '../../../shared/quote-pricing/quantity-selector';
import { QuoteAddToCartButton } from '../../../shared/quote-pricing/quote-add-to-cart-button';
import { QuoteLineSummary } from '../../../shared/quote-pricing/quote-line-summary';
import { QuoteLinesList } from '../../../shared/quote-pricing/quote-lines-list';
import { QuoteProductPriceItem } from '../models/quote-product-price-item.model';

import { QuoteProductHeader } from './quote-product-header';

interface Props {
   product: QuoteProductPriceItem;
   productDetailBaseRoute: string;
   documentsAnchor: string;
   channelName: string;
   validUntil: string;
   canViewPrices: boolean;
   showShoppingCartBtn: boolean;
   hasOrderingPermission: boolean;
   isOrderingBlocked: boolean;
   bracketPricingEnabled?: boolean;
   variant?: 'card' | 'flyout';
   leadTimeEnabled?: boolean;
   deliveryAddressId?: number;
   onAddToCart: (line: FlatQuotationLine, quantity: number) => void;
}

export const QuoteProductPriceDetails = ({
   product,
   productDetailBaseRoute,
   documentsAnchor,
   channelName,
   validUntil,
   canViewPrices,
   showShoppingCartBtn,
   hasOrderingPermission,
   isOrderingBlocked,
   bracketPricingEnabled = false,
   variant = 'card',
   leadTimeEnabled = false,
   deliveryAddressId,
   onAddToCart
}: Props) => {
   const translate = useTranslations();
   const format = useFormatter();

   const quotationLines = product.quotationLines;

   const { inputValue, onInputChange, hasSettled, resolution, selectQuantity, isSingleQuoteLine } = useQuotePricingState({
      quoteLines: quotationLines
   });

   const quoteQuantities = useMemo(() => getDistinctQuoteQuantities(quotationLines), [quotationLines]);
   const suggestions = useMemo<CombinedSuggestion[]>(() => getSuggestionRows(resolution), [resolution]);

   const { selectedLineId, selectedQuantity, selectedLinePackaging, isQuantityValid } = getQuoteSelectionMeta(resolution);
   const isBracketPricing = bracketPricingEnabled && hasBracketRanges(quotationLines);
   const uom = getQuantityUnitOfMeasure(quotationLines);

   const leadTimeByLineId = useMemo(() => {
      if (!leadTimeEnabled) return {};

      return getQuoteLineMtsLocalMap(product.leadTimeIndicators, quotationLines, {
         colorId: product.colorId,
         producerColorCode: product.producerColorCode,
         addressId: deliveryAddressId
      });
   }, [leadTimeEnabled, product.leadTimeIndicators, product.colorId, product.producerColorCode, quotationLines, deliveryAddressId]);

   const handleAddToCart = () => {
      if (resolution.kind !== 'quote') return;
      onAddToCart(resolution.line, resolution.quantity);
   };

   const detailsContent = (
      <>
         <QuoteProductHeader
            product={product}
            productDetailBaseRoute={productDetailBaseRoute}
            documentsAnchor={documentsAnchor}
            channelName={channelName}
         />

         <Box direction="column" gap="md">
            <Box justify="between" items="center" gap="sm">
               <Typography variant="body-highlight" color="subtle" id="pricing-table-label">
                  {translate('prices.pricing-table')}
               </Typography>
               <Badge variant="info" weight="regular">
                  {translate('prices.valid-until')} {validUntil ? format.dateTime(new Date(validUntil)) : '-'}
               </Badge>
            </Box>

            {!canViewPrices && <Typography>{translate('permissions.access-to-price-restricted-message')}</Typography>}

            {canViewPrices && quotationLines.length === 0 && (
               <Typography variant="small" color="subtle">
                  {translate('prices.no-quote')}
               </Typography>
            )}

            {canViewPrices && quotationLines.length > 0 && (
               <QuoteLinesList
                  quoteQuantities={quoteQuantities}
                  selectedLineId={selectedLineId}
                  selectedQuantity={selectedQuantity}
                  selectedLinePackaging={selectedLinePackaging}
                  isQuantityValid={isQuantityValid}
                  isSelectable={!isBracketPricing}
                  leadTimeByLineId={leadTimeByLineId}
                  onSelect={selectQuantity}
               />
            )}
         </Box>

         {canViewPrices && quotationLines.length > 0 && (
            <>
               {!isSingleQuoteLine && (
                  <QuantitySelector
                     value={inputValue}
                     suffix={uom}
                     error={hasSettled && resolution.kind === 'suggestion' && inputValue !== ''}
                     suggestions={hasSettled && inputValue !== '' ? suggestions : []}
                     onChange={onInputChange}
                     onSelect={selectQuantity}
                     id="quote-quantity-input"
                  />
               )}

               {resolution.kind === 'quote' && (
                  <QuoteLineSummary
                     line={resolution.line}
                     selectedQuantity={resolution.quantity}
                     hasMtsLocal={leadTimeByLineId[resolution.line.lineId]}
                  />
               )}
            </>
         )}
      </>
   );

   const addToCartButton = showShoppingCartBtn && (
      <QuoteAddToCartButton
         disabled={isOrderingBlocked || !canViewPrices || resolution.kind !== 'quote'}
         hasOrderingPermission={hasOrderingPermission}
         onAddToCart={handleAddToCart}
      />
   );

   if (variant === 'flyout') {
      return (
         <>
            <FlyoutContent>
               <Box direction="column" gap="lg">
                  {detailsContent}
               </Box>
            </FlyoutContent>
            {addToCartButton && <FlyoutActions>{addToCartButton}</FlyoutActions>}
         </>
      );
   }

   return (
      <Paper height="100%">
         <Box direction="column" gap="lg">
            {detailsContent}
            {addToCartButton}
         </Box>
      </Paper>
   );
};
